/**
 * Parses a date value into a local Date object.
 * Date-only strings like "2026-03-28" are parsed as UTC by JS, causing off-by-one
 * day errors in negative UTC offsets. Appending T00:00:00 forces local time parsing.
 */
export const parseLocalDate = date => {
  if (typeof date === "string" && /^\d{4}-\d{2}-\d{2}$/.test(date)) {
    return new Date(date + "T00:00:00");
  }
  return new Date(date);
};

export const DATE_FORMATS = {
  // ISO format - commonly used in APIs, databases, and backend communication
  ISO: "YYYY-MM-DD",

  // Short readable format - user-friendly format for UI display (e.g., "Jan 01, 2025")
  SHORT_READABLE: "MMM DD, YYYY",

  // Compact timestamp - useful for file names, logs, or IDs where no separators are preferred
  COMPACT_TIMESTAMP: "DDHHmmss",
};

/**
 * Formats a Date object or timestamp into a custom string format.
 *
 * Supported format tokens:
 * - YYYY: 4-digit year
 * - MMMM: Full month name (e.g., "January")
 * - MMM: Short month name (e.g., "Jan")
 * - MM: 2-digit month (e.g., "01")
 * - DD: 2-digit day (e.g., "09")
 * - HH: 2-digit hour in 24h format (e.g., "05", "17")
 * - mm: 2-digit minutes (e.g., "07")
 * - ss: 2-digit seconds (e.g., "59")
 *
 * @param date - A Date object or timestamp.
 * @param format - A format string using supported tokens.
 * @returns A formatted date string.
 */
export const formatDate = (date, format) => {
  const dt = typeof date === "number" ? new Date(date) : date;

  if (!Object.values(DATE_FORMATS).includes(format)) throw "Unsupported format: INVALID_FORMAT";

  if (!(dt instanceof Date) || isNaN(dt.getTime())) {
    throw new Error("Invalid Date object provided.");
  }

  // Extract components
  const year = dt.getFullYear();
  const month = String(dt.getMonth() + 1).padStart(2, "0");
  const day = String(dt.getDate()).padStart(2, "0");
  const hour = String(dt.getHours()).padStart(2, "0");
  const minutes = String(dt.getMinutes()).padStart(2, "0");
  const seconds = String(dt.getSeconds()).padStart(2, "0");
  const monthShort = dt.toLocaleString("en-US", { month: "short" });
  const monthLong = dt.toLocaleString("en-US", { month: "long" });

  // Replace format tokens
  const formattedDate = format
    .replace("YYYY", year.toString())
    .replace("MMMM", monthLong)
    .replace("MMM", monthShort)
    .replace("MM", month)
    .replace("DD", day)
    .replace("HH", hour)
    .replace("mm", minutes)
    .replace("ss", seconds);

  return formattedDate;
};

/**
 * Return UTC dateTime in DDHHmmss format
 * @returns {string}
 */
export const getUTCDDHHmmss = () => {
  const nowUtc = new Date(Date.now());
  return formatDate(nowUtc, DATE_FORMATS.COMPACT_TIMESTAMP); // e.g. "17093345" for April 17, 2023 at 09:33:45 UTC
};

export const generateDaysArray = (length = 31) => {
  const suffixes = ["th", "st", "nd", "rd"];

  return Array.from({ length }, (_, i) => {
    const day = i + 1;
    const suffix =
      day % 10 === 1 && day !== 11
        ? suffixes[1]
        : day % 10 === 2 && day !== 12
          ? suffixes[2]
          : day % 10 === 3 && day !== 13
            ? suffixes[3]
            : suffixes[0];

    return {
      label: `${day}${suffix}`,
      value: day,
    };
  });
};

/**
 * Returns a formatted date based on provided dayOfMonth and defaultTrialDays logic.
 *
 * @param {number} dayOfMonth - Day of month (1 to 28)
 * @param {number} defaultTrialDays - Number of days ahead to compare
 * @returns {string} - Formatted date string (e.g., "Jun 3, 2025")
 */
export const deriveCustomRenewalDate = (
  dayOfMonth,
  defaultTrialDays = 0,
  baseDate = new Date()
) => {
  if (dayOfMonth < 1 || dayOfMonth > 28) {
    throw new Error("Day must be between 1 and 28");
  }

  const today = baseDate;

  // 1. Create thresholdDate = today + defaultTrialDays
  const thresholdDate = new Date(today);
  thresholdDate.setDate(today.getDate() + defaultTrialDays);

  // 2. Use thresholdDate's year and month
  let targetYear = thresholdDate.getFullYear();
  let targetMonth = thresholdDate.getMonth(); // 0-indexed (0 = Jan)

  // 3. Create candidate date
  let candidateDate = new Date(targetYear, targetMonth, dayOfMonth);

  // 4. If candidate date is before thresholdDate, move to next month
  if (candidateDate < thresholdDate) {
    targetMonth += 1;
    if (targetMonth > 11) {
      // December overflow
      targetMonth = 0;
      targetYear += 1;
    }
    candidateDate = new Date(targetYear, targetMonth, dayOfMonth);
  }

  return candidateDate;
};

/**
 * function to get the difference in days between two dates
 * @param {Date} startDate - The start date
 * @param {Date} endDate - The end date
 * @returns {number} - The difference in days
 */
export const absoluteDayDiff = (startDate, endDate) => {
  if (!(startDate instanceof Date) || !(endDate instanceof Date)) {
    throw new Error("Invalid date input");
  }

  const msPerDay = 24 * 60 * 60 * 1000;
  const utc1 = Date.UTC(startDate.getFullYear(), startDate.getMonth(), startDate.getDate());
  const utc2 = Date.UTC(endDate.getFullYear(), endDate.getMonth(), endDate.getDate());

  return Math.abs(Math.floor((utc2 - utc1) / msPerDay));
};

export const isDateInPast = date => new Date(date) < new Date();
